/**
 * Flatten Fabric annotation overlays onto the source PDF and download.
 * Stamps a transparent PNG overlay per page (preserves base PDF content).
 */

import { PDFDocument } from '@cantoo/pdf-lib';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import fabric from '@/lib/fabric';
import type { FabricJson } from '@/lib/pdf/pdfAnnotations';
import { embedAnnotationImageSrcs } from '@/lib/pdf/pdfAnnotations';
import { copyArrayBuffer } from '@/lib/pdf/pdfDocument';
import {
  applyPdfEncryption,
  getProtectionPassword,
  type PdfEncryptionAlgorithm,
} from '@/lib/pdf/pdfProtection';

const EXPORT_MAX_EDGE = 2200;

function bytesForPdfLib(sourceBytes: ArrayBuffer): Uint8Array {
  try {
    return new Uint8Array(copyArrayBuffer(sourceBytes));
  } catch {
    throw new Error(
      'PDF source data is no longer available. Close and reopen the PDF, then download again.'
    );
  }
}

function dataUrlToUint8(dataUrl: string): Uint8Array {
  const [, b64] = dataUrl.split(',');
  const bin = atob(b64 || '');
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function waitFrames(n = 2): Promise<void> {
  return new Promise((resolve) => {
    const step = (left: number) => {
      if (left <= 0) resolve();
      else requestAnimationFrame(() => step(left - 1));
    };
    step(n);
  });
}

function countObjects(json: FabricJson | null | undefined): number {
  if (!json || !Array.isArray(json.objects)) return 0;
  return (json.objects as unknown[]).length;
}

function loadFabricJson(
  canvas: fabric.Canvas,
  json: FabricJson
): Promise<void> {
  const payload = JSON.parse(JSON.stringify(json)) as FabricJson;
  // Drop empty background — Fabric can treat "" oddly during enliven
  if (payload.background === '') delete payload.background;

  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    try {
      const result = canvas.loadFromJSON(payload, finish) as unknown;
      if (
        result &&
        typeof result === 'object' &&
        typeof (result as Promise<unknown>).then === 'function'
      ) {
        void (result as Promise<unknown>).then(finish).catch(reject);
      }
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Rasterize a Fabric annotation JSON to a transparent PNG (Uint8Array).
 * Uses an interactive Canvas (same path as Send-to-canvas) and verifies
 * every object was enlivened before capture.
 */
export async function rasterizeAnnotationOverlay(
  json: FabricJson,
  targetWidth?: number,
  targetHeight?: number
): Promise<Uint8Array | null> {
  const expected = countObjects(json);
  if (expected === 0) return null;

  const storedW = Math.max(1, Number(json.width) || 800);
  const storedH = Math.max(1, Number(json.height) || 1000);

  let width = targetWidth || storedW;
  let height = targetHeight || storedH;

  // Upscale for sharper export when source overlay was small
  const maxEdge = Math.max(width, height);
  if (maxEdge < EXPORT_MAX_EDGE) {
    const boost = Math.min(EXPORT_MAX_EDGE / maxEdge, 2.5);
    width = Math.round(width * boost);
    height = Math.round(height * boost);
  }

  const el = document.createElement('canvas');
  el.width = width;
  el.height = height;

  // Interactive Canvas matches pdfToCanvasProject overlay path (more reliable
  // for paths / text than StaticCanvas alone in some Fabric 5 builds).
  const canvas = new fabric.Canvas(el, {
    width,
    height,
    selection: false,
    enableRetinaScaling: false,
    renderOnAddRemove: false,
    preserveObjectStacking: true,
  });

  try {
    // Re-embed any non-data image srcs using live pixels is N/A here — bake only
    // has JSON. Placement/commit now stores data: URLs for signs/images.
    const payload = embedAnnotationImageSrcs(
      JSON.parse(JSON.stringify(json)) as FabricJson
    );
    await loadFabricJson(canvas, payload);
    await waitFrames(1);

    let loaded = canvas.getObjects().length;
    if (loaded < expected) {
      // Fallback: enliven objects explicitly and add one-by-one
      canvas.clear();
      const rawObjects = (payload.objects as unknown[]) || [];
      await new Promise<void>((resolve, reject) => {
        try {
          const enliven = fabric.util.enlivenObjects as (
            objects: object[],
            callback: (enlivened: fabric.Object[]) => void,
            namespace?: string
          ) => void;
          enliven(
            JSON.parse(JSON.stringify(rawObjects)) as object[],
            (objs) => {
              objs.forEach((obj) => {
                obj.set({ selectable: false, evented: false });
                canvas.add(obj);
              });
              resolve();
            },
            'fabric'
          );
        } catch (err) {
          reject(err);
        }
      });
      loaded = canvas.getObjects().length;
    }

    if (loaded < expected) {
      console.warn(
        `PDF overlay rasterize: expected ${expected} objects, loaded ${loaded}`
      );
    }
    if (loaded === 0) return null;

    const sx = width / storedW;
    const sy = height / storedH;
    if (Math.abs(sx - 1) > 0.001 || Math.abs(sy - 1) > 0.001) {
      canvas.getObjects().forEach((obj) => {
        obj.scaleX = (obj.scaleX || 1) * sx;
        obj.scaleY = (obj.scaleY || 1) * sy;
        obj.left = (obj.left || 0) * sx;
        obj.top = (obj.top || 0) * sy;
        obj.setCoords();
      });
    }

    // Ensure images finished decoding; fail loudly if a sign/image has no pixels
    const images = canvas.getObjects().filter((o) => o.type === 'image');
    await Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            const elImg = (img as fabric.Image).getElement?.() as
              | HTMLImageElement
              | undefined;
            if (!elImg) {
              console.warn('PDF overlay rasterize: image object has no element');
              resolve();
              return;
            }
            if (elImg.complete && elImg.naturalWidth > 0) {
              resolve();
              return;
            }
            const done = () => {
              if (!elImg.naturalWidth) {
                console.warn(
                  'PDF overlay rasterize: signature/image failed to decode',
                  typeof (img as fabric.Image).getSrc === 'function'
                    ? (img as fabric.Image).getSrc()?.slice?.(0, 48)
                    : elImg.src?.slice?.(0, 48)
                );
              }
              resolve();
            };
            elImg.addEventListener('load', done, { once: true });
            elImg.addEventListener('error', done, { once: true });
            setTimeout(done, 2500);
          })
      )
    );

    canvas.setWidth(width);
    canvas.setHeight(height);
    canvas.discardActiveObject();
    canvas.renderAll();
    await waitFrames(2);
    canvas.renderAll();

    const dataUrl = canvas.toDataURL({
      format: 'png',
      enableRetinaScaling: false,
      multiplier: 1,
    });

    if (!dataUrl || dataUrl.length < 200) return null;
    return dataUrlToUint8(dataUrl);
  } finally {
    canvas.dispose();
  }
}

/** Stamp all page overlays into the PDF; returns bytes + how many pages were stamped. */
export async function bakeAnnotationsIntoPdf(options: {
  sourceBytes: ArrayBuffer;
  pageCount: number;
  pageAnnotations: Record<number, FabricJson>;
  protection?: { enabled: boolean; algorithm: PdfEncryptionAlgorithm } | null;
}): Promise<{ bytes: ArrayBuffer; stamped: number }> {
  const { sourceBytes, pageCount, pageAnnotations, protection } = options;
  const pdfDoc = await PDFDocument.load(bytesForPdfLib(sourceBytes));

  let stamped = 0;
  let totalObjects = 0;

  for (let i = 0; i < pageCount; i++) {
    const json =
      pageAnnotations[i] ??
      (pageAnnotations as Record<string, FabricJson>)[String(i)];
    const n = countObjects(json);
    if (n === 0) continue;
    totalObjects += n;

    const overlayBytes = await rasterizeAnnotationOverlay(json!);
    if (!overlayBytes) {
      console.warn(`PDF bake: page ${i} had ${n} objects but rasterize returned empty`);
      continue;
    }

    const png = await pdfDoc.embedPng(overlayBytes);
    const pdfPage = pdfDoc.getPage(i);
    const { width: pw, height: ph } = pdfPage.getSize();
    pdfPage.drawImage(png, { x: 0, y: 0, width: pw, height: ph });
    stamped += 1;
  }

  if (stamped === 0) {
    console.warn('PDF bake: no annotation overlays were stamped');
  } else {
    console.info(
      `PDF bake: stamped ${stamped} page(s), ${totalObjects} annotation object(s)`
    );
  }

  if (protection?.enabled) {
    const password = getProtectionPassword();
    if (!password) {
      throw new Error(
        'Password protection is enabled but no password is set. Open Protect and set a password.'
      );
    }
    applyPdfEncryption(pdfDoc, {
      password,
      algorithm: protection.algorithm,
    });
  }

  const saved = await pdfDoc.save();
  const copy = new Uint8Array(saved.byteLength);
  copy.set(saved);
  return { bytes: copy.buffer, stamped };
}

export async function exportAnnotatedPdf(options: {
  sourceBytes: ArrayBuffer;
  pdfProxy: PDFDocumentProxy;
  pageAnnotations: Record<number, FabricJson>;
  filename: string;
  /** When set + session password present, encrypt the downloaded PDF */
  protection?: { enabled: boolean; algorithm: PdfEncryptionAlgorithm } | null;
}): Promise<Blob> {
  const { sourceBytes, pdfProxy, pageAnnotations, protection } = options;
  const { bytes } = await bakeAnnotationsIntoPdf({
    sourceBytes,
    pageCount: pdfProxy.numPages,
    pageAnnotations,
    protection,
  });
  return new Blob([bytes], { type: 'application/pdf' });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Count annotated pages (for UI feedback). */
export function countAnnotatedPages(
  pageAnnotations: Record<number, FabricJson>
): number {
  return Object.values(pageAnnotations).filter((j) => countObjects(j) > 0).length;
}
